import React, { useEffect, useState } from "react";
import { collection, getDocs, deleteDoc, doc, setDoc, Timestamp } from "firebase/firestore";
import { db, auth } from "../../firebase/firebaseConfig";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  Divider,
  Pagination,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import SidebarLayout from "../../components/layout/SidebarLayout";
import { getApiUrl } from "../../utils/api";

import ViewApplicantModal from "../../components/applicants/ViewApplicantModal";
import ApproveApplicantModal from "../../components/applicants/ApproveApplicantModal";
import RejectApplicantModal from "../../components/applicants/RejectApplicantModal";
import DeleteApplicantModal from "../../components/applicants/DeleteApplicantModal";

const ApplicantListPage = () => {
  const [aspirantes, setAspirantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // const [deleteConfirmText, setDeleteConfirmText] = useState("");
  // const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(null); // "approve" | "reject"
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  // Comment: state to control export modal and date range for approved applicants
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [fromApprovalDate, setFromApprovalDate] = useState("");
  const [toApprovalDate, setToApprovalDate] = useState("");
  const rowsPerPage = 10;
  const getCvFileName = (a) => {
    const base = a?.CV_FILE_NAME || `CV_${(a?.nombre||'').trim()}_${(a?.apellido||'').trim()}`;
    // Garantiza extensión si no viene del origen
    if (base && /\.(pdf|doc|docx)$/i.test(base)) return base;
    return `${base||'CV'}.pdf`;
  };

  const getCvUrl = (a) => {
    // Prioridad: nuevo campo -> legado en camelCase -> posible snake_case
    return a?.CV_URL || a?.cvLink || a?.cv_url || null;
  };

  const fetchAspirantes = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "aspirantes"));
      const data = querySnapshot.docs.map(doc => ({ firebaseId: doc.id, ...doc.data() }));
      setAspirantes(data);
    } catch (error) {
      console.error("Error al obtener aspirantes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAspirantes();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timeout = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timeout);
    }
  }, [successMessage]);

const handleApprove = async (applicant) => {
    if (!applicant) return;
    try {
      // Obtener el usuario actual
      const currentUser = auth.currentUser?.email || "desconocido";

      // Obtener el máximo ID de miembros
      const snapshot = await getDocs(collection(db, "miembros"));
      const miembros = snapshot.docs.map(doc => doc.data());
      const maxID = miembros.reduce((max, m) => Math.max(max, Number(m.ID || 0)), 0) + 1;
      const maxIDSocio = miembros.reduce((max, m) => Math.max(max, Number(m.ID_SOCIO || 0)), 0) + 1;

      // Construir el objeto miembro
      const nuevoMiembro = {
        ID: maxID,
        ID_SOCIO: maxIDSocio,
        "NOMBRE_APELLIDO": `${applicant.nombre} ${applicant.apellido}`,
        "NOMBRES": `${applicant.nombre}`,
        "APELLIDOS": `${applicant.apellido}`,
        "PAIS": applicant.country || "",
        "IDENTIFICACION": applicant.numero_documento || "",
        "LUGAR_FECHA_NACIMIENTO": applicant.birth_date || "",
        "TELEFONOS": applicant.telefono_movil || "",
        "EMAIL": applicant.user_email || "",
        "GENERO": applicant.genero || "",
        "TIPO_SOCIO": "ADHERENTE",
        "FECHA_MEMBRESIA": new Date().toISOString().split("T")[0],
        "CARGO": applicant.cargo || "",
        "CARGOS_DESEMPENADOS_GCIAMT": "",
        "FORMACION_PROFESIONAL": applicant.titulo_profesional || "",
        "DIRECCION_LABORAL": applicant.direccion_laboral || "",
        "INSTITUCION_LABORAL": applicant.institucion_laboral_actual || "",
        "MIEMBRO_OTRAS_SOCIEDADES": applicant.otras_asociaciones || "",
        "ID_ASPIRANTE": applicant.firebaseId || ""
      };

      // Crear el miembro
      const miembroDocRef = doc(db, "miembros", String(maxIDSocio));
      await setDoc(miembroDocRef, nuevoMiembro);

      // Marcar como aprobado
      await setDoc(doc(db, "aspirantes", applicant.firebaseId), {
        ...applicant,
        fecha_aprobacion: Timestamp.now(),
        usuario_aprobacion: currentUser,
        id_miembro: String(maxIDSocio),
        id_miembro_firebase: miembroDocRef.id,
      }); 

     // Save email pending in the "envio_emails" collection
      await setDoc(doc(collection(db, "envio_emails")), {
        nombre: applicant.nombre,
        apellido: applicant.apellido,
        email: applicant.user_email,
        enviado: false,
        fecha_creacion: Timestamp.now(),
        fecha_envio: null,
        tipo: "approve"
      });

      await fetchAspirantes();
      setSuccessMessage("El aspirante ha sido aprobado y agregado como miembro.");
      setSelectedApplicant(null);
    } catch (error) {
      console.error("Error al aprobar el aspirante:", error);
    }
  };

  const handleReject = async (applicant) => {
    if (!applicant) return;
    try {
      const currentUser = auth.currentUser?.email || "desconocido";

      await setDoc(doc(db, "aspirantes", applicant.firebaseId), {
        ...applicant,
        fecha_rechazo: Timestamp.now(),
        usuario_rechazo: currentUser
      });

      await fetchAspirantes();

      setSuccessMessage("La solicitud ha sido rechazada.");
      setSelectedApplicant(null);
    } catch (error) {
      console.error("Error al rechazar el aspirante:", error);
    }
  };

  // Delete handler for modal
  const handleDelete = async (applicant) => {
    if (!applicant?.firebaseId) {
      console.error("ID del aspirante no definido. No se puede borrar.");
      return;
    }
    try {
      // Borrar el documento de aspirante
      const docRef = doc(db, "aspirantes", applicant.firebaseId);
      await deleteDoc(docRef);
      await fetchAspirantes();
      setSelectedApplicant(null);
      setSuccessMessage("El aspirante ha sido eliminado correctamente.");
    } catch (error) {
      console.error("Error al borrar el aspirante:", error);
    }
  };

  // Comment: handle export of approved applicants into a CSV file readable by Excel
  const handleExportApproved = () => {
    if (!fromApprovalDate) {
      // Comment: require at least a starting approval date
      alert("Por favor, seleccione la fecha DESDE de aprobación.");
      return;
    }

    // Comment: build Date objects for range filtering (inclusive)
    const from = new Date(fromApprovalDate);
    from.setHours(0, 0, 0, 0);

    const to = toApprovalDate ? new Date(toApprovalDate) : new Date();
    to.setHours(23, 59, 59, 999);

    // Comment: filter only approved applicants with fecha_aprobacion within the range
    const approvedApplicants = aspirantes.filter((a) => {
      if (!a.fecha_aprobacion?.toDate) return false;
      const d = a.fecha_aprobacion.toDate();
      return d >= from && d <= to;
    });

    if (approvedApplicants.length === 0) {
      alert("No se encontraron aspirantes aprobados en el rango de fechas seleccionado.");
      return;
    }

    // Comment: define CSV headers
    const headers = [
      "Nombre",
      "Apellido",
      "Email",
      "País",
      "Fecha Aprobación",
      "ID Miembro"
    ];

    // Comment: build CSV rows from approved applicants
    const rows = approvedApplicants.map((a) => [
      a.nombre || "",
      a.apellido || "",
      a.user_email || "",
      a.pais_nacimiento || a.country || "",
      a.fecha_aprobacion?.toDate?.().toLocaleDateString?.() || "",
      a.id_miembro || ""
    ]);

    // Comment: join headers and rows into a CSV string (Excel-compatible)
    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => {
            const v = value == null ? "" : String(value);
            // Comment: escape double quotes and wrap each field in quotes
            const escaped = v.replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(";")
      )
      .join("\r\n");

    // Comment: create a Blob and trigger download in the browser
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const fileNameFrom = fromApprovalDate;
    const fileNameTo = toApprovalDate || new Date().toISOString().slice(0, 10);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `aspirantes_aprobados_${fileNameFrom}_a_${fileNameTo}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Comment: close modal after download
    setExportModalOpen(false);
  };
  // Filtrado
  const filtered = aspirantes
    .filter(a => {
      const term = searchTerm.toLowerCase();
      return (
        (a.nombre || "").toLowerCase().includes(term) ||
        (a.apellido || "").toLowerCase().includes(term) ||
        (a.user_email || "").toLowerCase().includes(term) ||
        (a.pais_nacimiento || "").toLowerCase().includes(term)
      );
    })
    .filter(a => {
      const isAtendida = a.fecha_aprobacion || a.fecha_rechazo;
      return tabIndex === 0 ? !isAtendida : isAtendida;
    });
  // Paginación
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <SidebarLayout>
      <Box display="flex" justifyContent="center" mt={4}>
        <Box sx={{ width: "100%", maxWidth: 1000 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h4" gutterBottom>
              Solicitudes de Membresía
            </Typography>
            {/* Comment: button to open modal for exporting approved applicants */}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => setExportModalOpen(true)}
            >
              Exportar aprobados
            </Button>
          </Box>
          {successMessage && (
            <Box sx={{ my: 2, p: 2, bgcolor: "#dff0d8", color: "#3c763d", borderRadius: 1 }}>
              {successMessage}
            </Box>
          )}
          <Tabs value={tabIndex} onChange={(e, newValue) => setTabIndex(newValue)} sx={{ mb: 2 }}>
            <Tab label="Solicitudes Pendientes" />
            <Tab label="Solicitudes Atendidas" />
          </Tabs>
          <TextField
            label="Buscar solicitante"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><b>Nombre</b></TableCell>
                    <TableCell><b>Apellido</b></TableCell>
                    <TableCell><b>Email</b></TableCell>
                    <TableCell><b>CV</b></TableCell>
                    {tabIndex === 0 ? (
                      <>
                        <TableCell><b>País</b></TableCell>
                        <TableCell><b>Fecha de Nacimiento</b></TableCell>
                        <TableCell><b>Fecha</b></TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell><b>Fecha Aprob./Rech.</b></TableCell>
                        <TableCell><b>Usuario</b></TableCell>
                      </>
                    )}
                    <TableCell><b>Acciones</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No se encontraron resultados.
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.nombre}</TableCell>
                        <TableCell>{a.apellido}</TableCell>
                        <TableCell>{a.user_email}</TableCell>
                        <TableCell>
                          {(() => {
                            const url = getCvUrl(a);
                            return url ? (
                              <Button
                                component="a"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={getCvFileName(a)}
                                size="small"
                                startIcon={<DownloadIcon />}
                              >
                                Descargar
                              </Button>
                            ) : (
                              "—"
                            );
                          })()}
                        </TableCell>
                        {tabIndex === 0 ? (
                          <>
                            <TableCell>{a.pais_nacimiento}</TableCell>
                            <TableCell>{a.birth_date}</TableCell>
                            <TableCell>{a.timestamp?.seconds ? new Date(a.timestamp.seconds * 1000).toLocaleDateString() : ""}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>
                              {a.fecha_aprobacion?.toDate?.().toLocaleDateString?.() || a.fecha_rechazo?.toDate?.().toLocaleDateString?.() || ""}
                              {a.id_miembro_firebase && (
                                <IconButton
                                  color="primary"
                                  component="a"
                                  href={`/members/${a.id_miembro_firebase}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label="ver miembro"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>{a.usuario_aprobacion || a.usuario_rechazo || ""}</TableCell>
                          </>
                        )}
                        <TableCell>
                          <IconButton onClick={() => setSelectedApplicant(a)} aria-label="ver">
                            <VisibilityIcon />
                          </IconButton>
                          {tabIndex === 0 && (
                            <>
                              <IconButton
                                color="success"
                                onClick={() => {
                                  setSelectedApplicant(null);
                                  setConfirmAction("approve");
                                  setSelectedApplicant(a);
                                  setConfirmOpen(true);
                                }}
                                aria-label="aprobar"
                              >
                                <CheckIcon />
                              </IconButton>
                              <IconButton
                                color="warning"
                                onClick={() => {
                                  setSelectedApplicant(null);
                                  setConfirmAction("reject");
                                  setSelectedApplicant(a);
                                  setConfirmOpen(true);
                                }}
                                aria-label="rechazar"
                              >
                                <ClearIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setSelectedApplicant(null);
                                  setConfirmAction("delete");
                                  setSelectedApplicant(a);
                                  setConfirmOpen(true);
                                }}
                                aria-label="borrar"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
          )}
          <Pagination
            count={Math.max(1, Math.ceil(filtered.length / rowsPerPage))}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            sx={{ mt: 2, display: "flex", justifyContent: "center" }}
          />
        </Box>
      </Box>
      {/* Modals for view, approve, reject, delete */}
      <ViewApplicantModal
        open={!!selectedApplicant && (!confirmOpen || confirmAction === null)}
        onClose={() => setSelectedApplicant(null)}
        applicant={selectedApplicant}
      />
      <ApproveApplicantModal
        open={confirmOpen && confirmAction === "approve"}
        onClose={() => setConfirmOpen(false)}
        applicant={selectedApplicant}
        onApprove={handleApprove}
      />
      <RejectApplicantModal
        open={confirmOpen && confirmAction === "reject"}
        onClose={() => setConfirmOpen(false)}
        applicant={selectedApplicant}
        onReject={handleReject}
      />
      <DeleteApplicantModal
        open={confirmOpen && confirmAction === "delete"}
        onClose={() => setConfirmOpen(false)}
        applicant={selectedApplicant}
        onDelete={handleDelete}
      />
      <Dialog
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Exportar solicitantes aprobados</DialogTitle>
        <DialogContent>
          <Box mt={2} display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Desde fecha de aprobación"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fromApprovalDate}
              onChange={(e) => setFromApprovalDate(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Hasta fecha de aprobación (opcional)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={toApprovalDate}
              onChange={(e) => setToApprovalDate(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportModalOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleExportApproved}>
            Descargar
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
};

export default ApplicantListPage;